import React, { useCallback, useMemo, useState } from "react";
import {
  BlockStack,
  Button,
  Card,
  ChoiceList,
  Icon,
  InlineStack,
  Text,
  TextField,
  Tooltip,
  Banner,
} from "@shopify/polaris";
import { ExternalIcon } from "@shopify/polaris-icons";
import { useEnsureInstall } from "../../hooks/useEnsureInstall";
import { openRemoteUrl } from "../../utils/openRemoteUrl";
import { getThemeEditorBlockDeepLink } from "../../utils/themeEditorLinks";
import {
  getDefaultPagesForPlacement,
  getDefaultPositionLabel,
  resolvePositionMode,
} from "../../utils/placementDefaults";

const BOOSTER_BLOCK_NAME = "Free Shipping Bar";

export default function BarSetupSection({ publicId, placementType, placement, onPatchPlacement }) {
  const { shop } = useEnsureInstall();
  const [copied, setCopied] = useState(false);

  const positionMode = useMemo(() => resolvePositionMode(placement), [placement]);
  const isCustom = positionMode === "custom";
  const defaultPositionLabel = getDefaultPositionLabel(placementType);

  const setPositionMode = useCallback(
    (mode) => {
      if (!onPatchPlacement) return;
      if (mode === "custom") {
        onPatchPlacement({ position_mode: "custom", pages: "custom_position" });
      } else {
        onPatchPlacement({
          position_mode: "default",
          pages: getDefaultPagesForPlacement(placementType),
        });
      }
    },
    [onPatchPlacement, placementType],
  );

  const copyBoosterId = useCallback(async () => {
    if (!publicId) return;
    try {
      await navigator.clipboard.writeText(publicId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.warn("[BarSetup] copy failed", e);
    }
  }, [publicId]);

  const openThemeBlockEditor = useCallback(() => {
    const url = getThemeEditorBlockDeepLink(shop, placementType || "product_page");
    if (url) openRemoteUrl(url, { newContext: true });
  }, [shop, placementType]);

  const positionChoices = useMemo(
    () => [
      {
        label: "Default position (recommended)",
        value: "default",
        helpText: defaultPositionLabel,
      },
      {
        label: "Custom position (theme block)",
        value: "custom",
        helpText:
          "Add the Free Shipping Bar app block in the theme editor and drag it where you want. Theme block position controls placement.",
      },
    ],
    [defaultPositionLabel],
  );

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingSm">
            Storefront position
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued">
            By default, this bar is placed automatically — no theme block required. Choose custom
            position only if you want full control in the theme editor.
          </Text>
          <ChoiceList
            title="Position mode"
            titleHidden
            choices={positionChoices}
            selected={[positionMode]}
            onChange={(v) => setPositionMode(v[0] || "default")}
          />
        </BlockStack>
      </Card>

      {isCustom ? (
        <>
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingSm">
                Custom placement with theme block
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                Automatic placement is off. Add the <strong>{BOOSTER_BLOCK_NAME}</strong> app block
                in the theme editor, paste your Booster ID, and drag the block to your preferred
                spot.
              </Text>
              {publicId ? (
                <InlineStack gap="200" wrap={false} blockAlign="end">
                  <div className="fsb-setup-id-field" style={{ flex: 1, minWidth: 0 }}>
                    <TextField label="Booster ID" value={publicId} readOnly autoComplete="off" />
                  </div>
                  <Tooltip
                    key={copied ? "booster-id-copied" : "booster-id-idle"}
                    content="Copied"
                    active={copied}
                  >
                    <Button onClick={() => void copyBoosterId()} variant="primary">
                      Copy ID
                    </Button>
                  </Tooltip>
                </InlineStack>
              ) : (
                <Text as="p" variant="bodyMd" tone="subdued">
                  Save & publish this bar to generate your Booster ID.
                </Text>
              )}
              <InlineStack>
                <Button variant="primary" icon={<Icon source={ExternalIcon} />} onClick={openThemeBlockEditor}>
                  Add block in Theme Editor
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>

          <Banner tone="warning">
            <p>
              In custom mode, moving the block in the theme editor controls placement. The default
              automatic position is not used.
            </p>
          </Banner>
        </>
      ) : null}
    </BlockStack>
  );
}
